// File: src/pages/LoginPage.tsx

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Alert, CircularProgress, Grid, CssBaseline
} from '@mui/material';
import { VpnKey as SsoIcon } from '@mui/icons-material';
import { useAppContext } from '../contexts/AppContext';
import { RandstadLogo } from '../components/RandstadLogo';

export default function LoginPage(): JSX.Element {
  const [loading, setLoading] = useState<boolean>(false);
  const { login, authError, clearAuthError } = useAppContext();

  // The authError from context is now the single source of truth for login errors.
  // The localError state has been removed.

  const handleLogin = async () => {
    clearAuthError(); // Clear any old errors from the context
    setLoading(true);
    try {
      await login();
      // On success, the AppContext will handle navigation
    } catch (err: any) {
      // This catches user-cancelled popups, etc.
      // The main auth error (e.g., 403 Forbidden) is handled in the context.
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      <CssBaseline />
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1554224155-1696413565d3?q=80&w=2070&auto=format&fit=crop)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: (t) =>
            t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}
        >
          <RandstadLogo sx={{ color: 'primary.main', height: 40, width: 'auto', mb: 2 }} />
          <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
            Reimbursement AI
          </Typography>
          <Box sx={{ mt: 6, width: '100%', maxWidth: '350px', textAlign: 'center' }}>
            {authError && <Alert severity="error" sx={{ width: '100%', mb: 2, textAlign: 'left' }}>{authError}</Alert>}
            
            <Typography variant="h6" sx={{ mb: 1 }}>Get Started</Typography>
            
            <Button 
              fullWidth 
              variant="contained" 
              sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }} 
              onClick={handleLogin}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SsoIcon />}
            >
              {loading ? 'Redirecting...' : 'Login'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Use Randstad Single Sign-on credentials
            </Typography>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
